import { isEmpty } from 'lodash';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Button, Card, Modal } from 'react-bootstrap';
import { useDispatch } from "react-redux";
import { Link } from 'react-router-dom';
import { Nullable } from "../../../../types/utils";
import * as Api from "../../../api";
import { OrderCategory, OrderCostingVersion, OrderStatus, TApiOrderDetails } from '../../../api/endpoints/Order';
import { TOrderProduct } from "../../../api/endpoints/OrderProduct";
import Col from '../../../components/Common/Col';
import Row from '../../../components/Common/Row';
import LoadingIndicator from "../../../components/Feedback/LoadingIndicator";
import {
  AdditionalChargesFootnote,
  EPUOldLicenseNotice, FeeWeaverAgreementFootnote,
  LimitCapacityFootnote,
  LimitCapacityFootnoteLegacy,
  ResearchAndSmallBusinessFootnote,
  ServicePackRecommendedFootnote
} from '../../../components/Footnotes';
import ExternalLink, { MailLink } from '../../../components/Navigation/Links/ExternalLink';
import OrderName from "../../../components/OrderName";
import OrderPDFButton from "../../../components/OrderPDFButton";
import OrderSubmissionForm from "../../../components/OrderSubmissionForm";
import OrderSubmissionFormCompatibility from "../../../components/OrderSubmissionFormCompatibility";
import Disclaimer from "../../../components/Ui/Disclaimer";
import { UserType } from '../../../entities/User';
import useOrderCostingVersion from "../../../hooks/useOrderCostingVersion";
import DisseminationRequestPolicy from "../../../policies/DisseminationRequestPolicy";
import OrderPolicy from '../../../policies/OrderPolicy';
import { paths } from "../../../router";
import { fetchOrderDetails } from "../../../store/action-creators/orders";
import DateTime from "../../../utils/DateTime";
import { formatVolumeBand } from "../../../utils/Format";
import OrderHelpers from "../../../utils/OrderHelpers";
import DailyVolumeExceededWarning from "../DailyVolumeExceededWarning";
import OrderExpirationAlert from "../ExpirationAlert";
import LicenceDetails from "../LicenceDetails";
import MaxChargeWarning from "../MaxChargeWarning";
import OrderCostSummary from "../OrderCostSummary";
import RealtimeOrderDetails from "../OrderDetails/RealtimeOrderDetails";
import OrderHeader from '../OrderHeader';
import { OrderProps } from '../types';
import ChangeCostingVersionButton from "./ChangeCostingVersionButton";

type Props = OrderProps & {
  order: TApiOrderDetails<OrderCategory.REALTIME>;
}

const shouldShowMaxChargeWarning = (order: TApiOrderDetails<OrderCategory.REALTIME>) => {
  return order.cost.data.total_cost && 'max_charge' in order.cost.data.total_cost && order.cost.data.total_cost.max_charge;
}

const RealtimeOrder = (props: Props) => {
  const {
    user,
    order,
    sets,
    chosenProvider,
    chosenDataDeliverer,
    chosenServicePackage,
    chosenVolumeBand,
    onSubmitClick,
    submissionDialogOpen,
    setSubmissionDialogOpen,
    onSubmissionComplete,
    onApprove,
    onReject,
    onCancel
  } = props;

  const [products, setProducts] = useState<Array<TOrderProduct<OrderCategory.REALTIME>>>([]);
  const [error, setError] = useState<Nullable<string>>(null)
  const dispatch = useDispatch();
  const hasCostError = Boolean(order.cost.data && order.cost.data.error);
  const { orderCostingVersions, currentOrderCostingVersion } = useOrderCostingVersion(order);
  const isOrderExpired = OrderHelpers.isExpired(order);
  const isOrderOverVolumeLimit = OrderHelpers.isOverVolumeLimit(order.cost, currentOrderCostingVersion);

  useEffect(() => {
    Api.OrderProduct.list(order.id)
      .then(setProducts)
      .catch(() => setError('Sorry! We were unable to fetch products.'));
  }, [order.id, order.updated]);

  const onProductDuplicate = useCallback((newProduct) => {
    Api.OrderProduct.list(order.id)
      .then(products => {
        setProducts(products);
        dispatch(fetchOrderDetails(order.id.toString(), true));
      })
      .catch(() => setError('Sorry! We were unable to fetch products.'));
  }, [dispatch, order.id]);

  const onProductRemove = useCallback((product) => {
    Api.OrderProduct.list(order.id)
      .then(products => {
        setProducts(products);
        dispatch(fetchOrderDetails(order.id, true));
      })
      .catch(() => setError('Sorry! We were unable to fetch products.'));
  }, [dispatch, order.id]);

  const onCostVersionChange = (version: OrderCostingVersion['version']) => {
    Api.Order.setCostingVersion(order.id, version).then(() => {
      dispatch(fetchOrderDetails(order.id, true));
    });
  }

  if (!orderCostingVersions) {
    return <LoadingIndicator />;
  }

  return (
    <Fragment>
      {
        isOrderExpired &&
        <OrderExpirationAlert />
      }

      <OrderHeader order={order} />

      <Row className="margin-bottom-xs">
        <Col>
          <strong>Order name</strong>: <OrderName order={order} />
        </Col>
      </Row>

      {
        user.type !== UserType.Customer &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Customer:</strong> {order.customer}
            &nbsp;(<MailLink email={order.email} />)
          </Col>
        </Row>
      }

      {
        order.start_date &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Start date:</strong> {DateTime.Format.dateLong(order.start_date)}
          </Col>
        </Row>
      }

      {
        chosenProvider &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Licensor:</strong> {chosenProvider.name}
          </Col>
        </Row>
      }

      {
        chosenDataDeliverer &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Data deliverer:</strong> {chosenDataDeliverer.name}
          </Col>
        </Row>
      }

      {
        chosenServicePackage &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Service Package:</strong> {chosenServicePackage.title}
          </Col>
        </Row>
      }

      {
        chosenVolumeBand &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Volume Band:</strong> {formatVolumeBand(chosenVolumeBand)} per day
          </Col>
        </Row>
      }

      <LicenceDetails order={order} user={user} />

      {
        user.type === UserType.Customer &&
        <Row className="margin-bottom-xs">

          <Col xs={12}>
            Click on the following link to read about
            {' '}
            <ExternalLink
              url="https://www.ecmwf.int/en/forecasts/accessing-forecasts/payment-rules-and-options/tariffs">
              data pricing
            </ExternalLink>
            .
          </Col>
        </Row>
      }


      <div style={{ display: 'flex', justifyContent: 'flex-end' }} className="margin-bottom-sm">
        {
          OrderPolicy.canSetCostingVersion(user, order) &&
          !isOrderExpired &&
          orderCostingVersions.length > 1 &&
          <ChangeCostingVersionButton
            order={order}
            availableVersions={orderCostingVersions}
            onVersionSelect={onCostVersionChange}
          />
        }
      </div>

      <Card className="margin-bottom-sm">
        <Card.Body>
          <RealtimeOrderDetails
            order={order}
            orderCostingVersions={orderCostingVersions}
            products={products}
            sets={sets}
            onProductDuplicate={onProductDuplicate}
            onProductRemove={onProductRemove}
          />

          {
            !isEmpty(order.cost) &&
            <OrderCostSummary
              order={order}
              currentCostingVersion={currentOrderCostingVersion}
              annualTotalCostFootnoteRef="*"
            />
          }


        </Card.Body>
      </Card>

      {
        shouldShowMaxChargeWarning(order) &&
        <div className="margin-bottom-sm">
          <MaxChargeWarning order={order} />
        </div>
      }

      {
        isOrderOverVolumeLimit && currentOrderCostingVersion &&
        <div className="margin-bottom-sm">
          <DailyVolumeExceededWarning costingVersion={currentOrderCostingVersion} />
        </div>
      }

      <Row>
        <Col>
          <div style={{ display: 'flex' }}>
            {
              OrderPolicy.canApprove(user, order) &&
              <Fragment>
                <Button
                  onClick={onApprove}
                  style={{ marginRight: '2em' }}
                  variant="success"
                >
                  Approve order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canReject(user, order) &&
              <Fragment>
                <Button
                  onClick={onReject}
                  style={{ marginRight: '2em' }}
                  variant="danger"
                >
                  Reject order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canCancel(user, order) &&
              <Fragment>
                <Button
                  onClick={onCancel}
                  style={{ marginRight: '2em' }}
                  variant="danger"
                >
                  Cancel order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canSubmit(user, order, orderCostingVersions) &&
              <Fragment>
                <Button
                  onClick={onSubmitClick}
                  style={{ marginRight: '2em' }}
                  disabled={hasCostError}
                >
                  Submit order
                </Button>
              </Fragment>
            }

            <Modal size="xl" className="realtime-order-submit-modal" show={submissionDialogOpen} onHide={() => setSubmissionDialogOpen(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Submit order</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <OrderSubmissionFormCompatibility order={order}>
                  {
                    currentOrderCostingVersion &&
                    <OrderSubmissionForm
                      key={[order.id, order.cost.version].join('-')}
                      order={order}
                      orderCostingVersion={currentOrderCostingVersion}
                      onComplete={onSubmissionComplete}
                    />
                  }
                </OrderSubmissionFormCompatibility>
              </Modal.Body>
            </Modal>


            {
              OrderPolicy.canAddProduct(user, order, orderCostingVersions) &&
              <Button
                as={Link}
                to={paths.realtimeOrderProductNew(order.id)}
                style={{ marginRight: '2em' }}
              >
                Add another product
              </Button>
            }

            <OrderPDFButton
              order={order}
              currentCostingVersion={currentOrderCostingVersion}
              orderProducts={products || []}
              sets={sets}
              provider={chosenProvider}
              dataDeliverer={chosenDataDeliverer}
              servicePackage={chosenServicePackage}
              volumeBand={chosenVolumeBand}
              disabled={!order || !products.length || !sets || hasCostError}
            />

            {
              DisseminationRequestPolicy.canAccess(user, order) && !hasCostError &&
              <Button
                as={Link}
                to={paths.orderDisseminationRequest(order.id)}
                style={{ marginLeft: '2em' }}
                variant="primary"
                className="btn__inverted"
              >
                Dissemination Request
              </Button>
            }
          </div>
        </Col>
      </Row>

      <div className="margin-bottom-xs margin-top-md">
        <Disclaimer identifier="*">
          {
            ['v1', 'v2', 'v3', 'v4', 'v5'].includes(order.cost.version) &&
            <Fragment>
              <p>
                <AdditionalChargesFootnote />
              </p>

              <p>
                <EPUOldLicenseNotice />
              </p>

              <p>
                <ResearchAndSmallBusinessFootnote />
              </p>

              <p>
                <LimitCapacityFootnoteLegacy />
              </p>
            </Fragment>
          }

          {
            order.cost.version === 'v6' &&
            <Fragment>
              <p>
                <LimitCapacityFootnote />
              </p>

              {
                order.status === OrderStatus.DRAFT &&
                <p>
                  <ServicePackRecommendedFootnote />
                </p>
              }

              {
                <p>
                  <FeeWeaverAgreementFootnote />
                </p>
              }
            </Fragment>
          }

        </Disclaimer>
      </div>
    </Fragment>
  );
}

export default RealtimeOrder;
